import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ArrowLeft, Clock, Star, Heart, Info } from 'lucide-react';
import { goHome, navigate } from '../utils/navigation';
import Footer from './Footer';

const Calendar: React.FC = () => {
  const [activeView, setActiveView] = useState<'calendar' | 'timeline'>('timeline');
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 8)); // September 2025
  const [currentSlide, setCurrentSlide] = useState(1);

  const images = [
    {
      src: "/calander/calander1.avif",
      alt: "Durga Puja Calendar - Traditional celebration scene"
    },
    {
      src: "/calander/calander2.avif",
      alt: "Durga Puja Calendar - Festival preparations and decorations"
    },
    {
      src: "/calander/calander3.avif",
      alt: "Durga Puja Calendar - Committee celebrations and rituals"
    },
    {
      src: "/calander/calander4.avif",
      alt: "Durga Puja Calendar - Traditional Durga Puja festivities"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  const goBack = () => {
    goHome();
  };

  const goToRegistration = () => {
    navigate('/signup');
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  // Calendar navigation functions
  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const hasEvent = (day: number | null) => {
    if (!day) return false;
    
    if (currentMonth.getMonth() === 8) { // September
      return [27, 28, 29, 30].includes(day);
    } else if (currentMonth.getMonth() === 9) { // October
      return [1, 2].includes(day);
    }
    return false;
  };

  const getEventType = (day: number | null) => {
    if (!day) return '';
    
    if (currentMonth.getMonth() === 8) { // September
      if (day === 27) return 'panchami';
      if (day === 28) return 'shashthi';
      if (day === 29) return 'saptami';
      if (day === 30) return 'ashtami';
    } else if (currentMonth.getMonth() === 9) { // October
      if (day === 1) return 'navami';
      if (day === 2) return 'dashami';
    }
    return '';
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      <Helmet>
        <title>Durga Puja Calendar 2025 | Durga Puja 2025 | Durga Puja Network</title>
        <meta name="description" content="Find key dates of Durga Puja in 2025. Mahalaya, Maha Panchami, Maha Shashthi, Maha Saptami, Maha Ashtami, Maha Navami, Vijaya Dashami. Durga Puja Network" />
        <link rel="canonical" href="https://www.durgapuja.network/Durga-Puja-Calendar-2025" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Durga Puja Calendar 2025 | Durga Puja 2025 | Durga Puja Network" />
        <meta property="og:description" content="Find key dates of Durga Puja in 2025. Mahalaya, Maha Panchami, Maha Shashthi, Maha Saptami, Maha Ashtami, Maha Navami, Vijaya Dashami. Durga Puja Network" />
        <meta property="og:url" content="https://www.durgapuja.network/Durga-Puja-Calendar-2025" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.durgapuja.network/calander/calander1.avif" />
        <meta property="og:image:alt" content="Durga Puja Calendar 2025 - Traditional celebration scene" />
        <meta property="og:site_name" content="Durga Puja Network" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Durga Puja Calendar 2025 | Durga Puja 2025 | Durga Puja Network" />
        <meta name="twitter:description" content="Find key dates of Durga Puja in 2025. Mahalaya, Maha Panchami, Maha Shashthi, Maha Saptami, Maha Ashtami, Maha Navami, Vijaya Dashami. Durga Puja Network" />
        <meta name="twitter:image" content="https://www.durgapuja.network/calander/calander1.avif" />
        <meta name="twitter:image:alt" content="Durga Puja Calendar 2025 - Traditional celebration scene" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="keywords" content="Durga Puja Calendar 2025, Mahalaya, Maha Panchami, Maha Shashthi, Maha Saptami, Maha Ashtami, Maha Navami, Vijaya Dashami, Durga Puja dates, Bengali festival, Hindu festival, Goddess Durga, Puja schedule" />
        <meta name="author" content="Durga Puja Network" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en" />
        
        {/* Structured Data for Events */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": "Durga Puja 2025",
            "description": "Find key dates of Durga Puja in 2025. Mahalaya, Maha Panchami, Maha Shashthi, Maha Saptami, Maha Ashtami, Maha Navami, Vijaya Dashami.",
            "startDate": "2025-09-21",
            "endDate": "2025-10-02",
            "location": {
              "@type": "Place",
              "name": "Worldwide",
              "address": "Global Durga Puja Celebrations"
            },
            "organizer": {
              "@type": "Organization",
              "name": "Durga Puja Network",
              "url": "https://www.durgapuja.network"
            },
            "image": "https://www.durgapuja.network/calander/calander1.avif",
            "url": "https://www.durgapuja.network/Durga-Puja-Calendar-2025"
          })}
        </script>
      </Helmet>
      {/* Hero Carousel Section */}
      <section className="relative h-96 md:h-[50rem] lg:h-[60rem] xl:h-[70rem] overflow-hidden">
        <div className="relative h-full">
          {images.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img 
                src={image.src} 
                alt={image.alt} 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50"></div>
            </div>
          ))}
        </div>
        
        {/* Hero Content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="font-runtime text-3xl sm:text-4xl md:text-5xl font-bold mb-4 animate-fade-in">
              Durga Puja Calendar 2025
            </h1>
            <p className="font-lucy-rose text-lg sm:text-xl animate-slide-up opacity-90">
              Plan your celebrations with the complete schedule of this UNESCO-recognized cultural heritage
            </p>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentSlide ? 'bg-dpn-pink' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="text-center mb-8">
            <button
              onClick={goBack}
              className="inline-flex items-center text-dpn-olive hover:text-dpn-pink transition-colors duration-200 mb-6 font-lucy-rose font-medium"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Home
            </button>
          </div>

          {/* View Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-full p-1 shadow-md">
              <button
                onClick={() => setActiveView('timeline')}
                className={`px-6 py-2 rounded-full font-lucy-rose font-medium transition-all duration-200 ${
                  activeView === 'timeline'
                    ? 'bg-dpn-pink text-white shadow-md'
                    : 'text-dpn-pink hover:bg-dpn-pink/10'
                }`}
              >
                Timeline View
              </button>
              <button
                onClick={() => setActiveView('calendar')}
                className={`px-6 py-2 rounded-full font-lucy-rose font-medium transition-all duration-200 ${
                  activeView === 'calendar'
                    ? 'bg-dpn-pink text-white shadow-md'
                    : 'text-dpn-pink hover:bg-dpn-pink/10'
                }`}
              >
                Calendar View
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Duration Card */}
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <Clock className="h-8 w-8 text-dpn-pink mx-auto mb-3" />
              <h3 className="font-runtime text-lg font-bold text-black mb-2">Duration</h3>
              <p className="font-lucy-rose text-gray-600">5 Core Days</p>
              <p className="font-lucy-rose text-sm text-gray-500">28 Sep - 2 Oct 2025</p>
            </div>

            {/* Peak Day Card */}
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <Star className="h-8 w-8 text-dpn-light-green mx-auto mb-3" />
              <h3 className="font-runtime text-lg font-bold text-black mb-2">Peak Day</h3>
              <p className="font-lucy-rose text-gray-600">Maha Ashtami</p>
              <p className="font-lucy-rose text-sm text-gray-500">30 September 2025</p>
            </div>

            {/* Farewell Card */}
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <Heart className="h-8 w-8 text-dpn-raspberry mx-auto mb-3" />
              <h3 className="font-runtime text-lg font-bold text-black mb-2">Farewell</h3>
              <p className="font-lucy-rose text-gray-600">Vijaya Dashami</p>
              <p className="font-lucy-rose text-sm text-gray-500">2 October 2025</p>
            </div>
          </div>

          {/* Timeline/Calendar Content */}
          {activeView === 'timeline' ? (
            <div className="space-y-6">
              {/* Mahalaya Event Card */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-dpn-olive p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        <div className="bg-white/20 rounded-full p-2 mr-4">
                          <CalendarIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-runtime text-2xl md:text-3xl font-bold text-white mb-1">Mahalaya</h2>
                          <p className="font-lucy-rose text-white/90 text-lg">Sunday, 21 September 2025</p>
                        </div>
                      </div>
                      <p className="font-lucy-rose text-white/95 text-lg mb-4 leading-relaxed">
                        Marks the start of the Devi Paksha and spiritual commencement of Durga Puja
                      </p>
                      <div className="inline-block bg-white/20 rounded-full px-4 py-2 mb-4">
                        <span className="font-lucy-rose text-white text-sm font-medium">Preparatory Phase</span>
                      </div>
                      <div className="bg-white/10 rounded-xl p-6">
                        <p className="font-lucy-rose text-white/95 leading-relaxed">
                          The invocation of Goddess Durga begins with the recitation of Chandipath and Mahishasura Mardini. This day sets the spiritual tone for the upcoming celebrations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Maha Panchami Event Card */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-dpn-light-green p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        <div className="bg-white/20 rounded-full p-2 mr-4">
                          <CalendarIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-runtime text-2xl md:text-3xl font-bold text-white mb-1">Maha Panchami</h2>
                          <p className="font-lucy-rose text-white/90 text-lg">Saturday, 27 September 2025</p>
                        </div>
                      </div>
                      <p className="font-lucy-rose text-white/95 text-lg mb-4 leading-relaxed">
                        Preparatory rituals and formal invitation (Bilva Nimantran)
                      </p>
                      <div className="inline-block bg-white/20 rounded-full px-4 py-2 mb-4">
                        <span className="font-lucy-rose text-white text-sm font-medium">Invitation Phase</span>
                      </div>
                      <div className="bg-white/10 rounded-xl p-6">
                        <p className="font-lucy-rose text-white/95 leading-relaxed">
                          The goddess is formally invited to descend to earth. Bilva leaves are used in the invitation ceremony, and preparatory rituals begin.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Maha Shashthi Event Card */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-dpn-pink p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        <div className="bg-white/20 rounded-full p-2 mr-4">
                          <CalendarIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-runtime text-2xl md:text-3xl font-bold text-white mb-1">Maha Shashthi</h2>
                          <p className="font-lucy-rose text-white/90 text-lg">Sunday, 28 September 2025</p>
                        </div>
                      </div>
                      <p className="font-lucy-rose text-white/95 text-lg mb-4 leading-relaxed">
                        Kalparambha, Bodhan - Formal start of Puja
                      </p>
                      <div className="inline-block bg-white/20 rounded-full px-4 py-2 mb-4">
                        <span className="font-lucy-rose text-white text-sm font-medium">Main Celebration Begins</span>
                      </div>
                      <div className="bg-white/10 rounded-xl p-6">
                        <p className="font-lucy-rose text-white/95 leading-relaxed">
                          The goddess is awakened (Bodhan) and the main puja rituals begin. This marks the official start of the five-day celebration.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Maha Saptami Event Card */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-dpn-raspberry p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        <div className="bg-white/20 rounded-full p-2 mr-4">
                          <CalendarIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-runtime text-2xl md:text-3xl font-bold text-white mb-1">Maha Saptami</h2>
                          <p className="font-lucy-rose text-white/90 text-lg">Monday, 29 September 2025</p>
                        </div>
                      </div>
                      <p className="font-lucy-rose text-white/95 text-lg mb-4 leading-relaxed">
                        Second day of main celebrations
                      </p>
                      <div className="inline-block bg-white/20 rounded-full px-4 py-2 mb-4">
                        <span className="font-lucy-rose text-white text-sm font-medium">Core Celebration</span>
                      </div>
                      <div className="bg-white/10 rounded-xl p-6">
                        <p className="font-lucy-rose text-white/95 leading-relaxed">
                          Elaborate rituals continue with special pujas and cultural programs. The goddess is worshipped with great devotion.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Maha Ashtami Event Card */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-gradient-to-r from-dpn-pink to-dpn-raspberry p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        <div className="bg-white/20 rounded-full p-2 mr-4">
                          <CalendarIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-runtime text-2xl md:text-3xl font-bold text-white mb-1">Maha Ashtami</h2>
                          <p className="font-lucy-rose text-white/90 text-lg">Tuesday, 30 September 2025</p>
                        </div>
                      </div>
                      <p className="font-lucy-rose text-white/95 text-lg mb-4 leading-relaxed">
                        Most significant day with Sandhi Puja and Kumari Puja
                      </p>
                      <div className="inline-block bg-white/20 rounded-full px-4 py-2 mb-4">
                        <span className="font-lucy-rose text-white text-sm font-medium">Peak Celebration</span>
                      </div>
                      <div className="bg-white/10 rounded-xl p-6">
                        <p className="font-lucy-rose text-white/95 leading-relaxed">
                          The most important day featuring Sandhi Puja at the junction of Ashtami and Navami, and Kumari Puja where young girls are worshipped.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Maha Navami Event Card */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-dpn-raspberry p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        <div className="bg-white/20 rounded-full p-2 mr-4">
                          <CalendarIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-runtime text-2xl md:text-3xl font-bold text-white mb-1">Maha Navami</h2>
                          <p className="font-lucy-rose text-white/90 text-lg">Wednesday, 1 October 2025</p>
                        </div>
                      </div>
                      <p className="font-lucy-rose text-white/95 text-lg mb-4 leading-relaxed">
                        Final day of worship before immersion
                      </p>
                      <div className="inline-block bg-white/20 rounded-full px-4 py-2 mb-4">
                        <span className="font-lucy-rose text-white text-sm font-medium">Final Worship</span>
                      </div>
                      <div className="bg-white/10 rounded-xl p-6">
                        <p className="font-lucy-rose text-white/95 leading-relaxed">
                          The last day of worship with special rituals and preparations for the farewell ceremony. Cultural programs reach their peak.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vijaya Dashami Event Card */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-gradient-to-r from-dpn-olive to-dpn-light-green p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-4">
                        <div className="bg-white/20 rounded-full p-2 mr-4">
                          <CalendarIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-runtime text-2xl md:text-3xl font-bold text-white mb-1">Vijaya Dashami</h2>
                          <p className="font-lucy-rose text-white/90 text-lg">Thursday, 2 October 2025</p>
                        </div>
                      </div>
                      <p className="font-lucy-rose text-white/95 text-lg mb-4 leading-relaxed">
                        Dashami/Visarjan - Farewell and immersion
                      </p>
                      <div className="inline-block bg-white/20 rounded-full px-4 py-2 mb-4">
                        <span className="font-lucy-rose text-white text-sm font-medium">Farewell & Immersion</span>
                      </div>
                      <div className="bg-white/10 rounded-xl p-6">
                        <p className="font-lucy-rose text-white/95 leading-relaxed">
                          The festival concludes with Sindoor Khela, farewell ceremonies, and the immersion of idols. A bittersweet end to the celebrations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* September 2025 Calendar */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="font-runtime font-bold text-xl text-center mb-4 text-gray-800">
                  September 2025
                </h3>
                
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 p-2">
                      {day}
                    </div>
                  ))}
                  
                  {/* September days - hardcoded */}
                  {/* Week 1 */}
                  <div className="text-center p-2 text-sm"></div>
                  <div className="text-center p-2 text-sm text-gray-800">1</div>
                  <div className="text-center p-2 text-sm text-gray-800">2</div>
                  <div className="text-center p-2 text-sm text-gray-800">3</div>
                  <div className="text-center p-2 text-sm text-gray-800">4</div>
                  <div className="text-center p-2 text-sm text-gray-800">5</div>
                  <div className="text-center p-2 text-sm text-gray-800">6</div>
                  
                  {/* Week 2 */}
                  <div className="text-center p-2 text-sm text-gray-800">7</div>
                  <div className="text-center p-2 text-sm text-gray-800">8</div>
                  <div className="text-center p-2 text-sm text-gray-800">9</div>
                  <div className="text-center p-2 text-sm text-gray-800">10</div>
                  <div className="text-center p-2 text-sm text-gray-800">11</div>
                  <div className="text-center p-2 text-sm text-gray-800">12</div>
                  <div className="text-center p-2 text-sm text-gray-800">13</div>
                  
                  {/* Week 3 */}
                  <div className="text-center p-2 text-sm text-gray-800">14</div>
                  <div className="text-center p-2 text-sm text-gray-800">15</div>
                  <div className="text-center p-2 text-sm text-gray-800">16</div>
                  <div className="text-center p-2 text-sm text-gray-800">17</div>
                  <div className="text-center p-2 text-sm text-gray-800">18</div>
                  <div className="text-center p-2 text-sm text-gray-800">19</div>
                  <div className="text-center p-2 text-sm text-gray-800">20</div>
                  
                  {/* Week 4 */}
                  <div className="bg-green-600 text-white rounded-lg p-2 text-sm font-bold text-center">
                    <div>21</div>
                    <div className="text-xs">Mahalaya</div>
                  </div>
                  <div className="text-center p-2 text-sm text-gray-800">22</div>
                  <div className="text-center p-2 text-sm text-gray-800">23</div>
                  <div className="text-center p-2 text-sm text-gray-800">24</div>
                  <div className="text-center p-2 text-sm text-gray-800">25</div>
                  <div className="text-center p-2 text-sm text-gray-800">26</div>
                  <div className="bg-green-600 text-white rounded-lg p-2 text-sm font-bold text-center">
                    <div>27</div>
                    <div className="text-xs">Panchami</div>
                  </div>
                  
                  {/* Week 5 */}
                  <div className="bg-dpn-raspberry text-white rounded-lg p-2 text-sm font-bold text-center">
                    <div>28</div>
                    <div className="text-xs">Shashthi</div>
                  </div>
                  <div className="bg-dpn-raspberry text-white rounded-lg p-2 text-sm font-bold text-center">
                    <div>29</div>
                    <div className="text-xs">Saptami</div>
                  </div>
                  <div className="bg-dpn-raspberry text-white rounded-lg p-2 text-sm font-bold text-center">
                    <div>30</div>
                    <div className="text-xs">Ashtami</div>
                  </div>
                </div>
              </div>

              {/* October 2025 Calendar */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="font-runtime font-bold text-xl text-center mb-4 text-gray-800">
                  October 2025
                </h3>
                
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 p-2">
                      {day}
                    </div>
                  ))}
                  
                  {/* October days - hardcoded */}
                  {/* Week 1 */}
                  <div className="text-center p-2 text-sm"></div>
                  <div className="text-center p-2 text-sm"></div>
                  <div className="text-center p-2 text-sm"></div>
                  <div className="bg-dpn-raspberry text-white rounded-lg p-2 text-sm font-bold text-center">
                    <div>1</div>
                    <div className="text-xs">Navami</div>
                  </div>
                  <div className="bg-green-600 text-white rounded-lg p-2 text-sm font-bold text-center">
                    <div>2</div>
                    <div className="text-xs">Dashami</div>
                  </div>
                  <div className="text-center p-2 text-sm text-gray-800">3</div>
                  <div className="text-center p-2 text-sm text-gray-800">4</div>
                  
                  {/* Week 2 */}
                  <div className="text-center p-2 text-sm text-gray-800">5</div>
                  <div className="text-center p-2 text-sm text-gray-800">6</div>
                  <div className="text-center p-2 text-sm text-gray-800">7</div>
                  <div className="text-center p-2 text-sm text-gray-800">8</div>
                  <div className="text-center p-2 text-sm text-gray-800">9</div>
                  <div className="text-center p-2 text-sm text-gray-800">10</div>
                  <div className="text-center p-2 text-sm text-gray-800">11</div>
                  
                  {/* Week 3 */}
                  <div className="text-center p-2 text-sm text-gray-800">12</div>
                  <div className="text-center p-2 text-sm text-gray-800">13</div>
                  <div className="text-center p-2 text-sm text-gray-800">14</div>
                  <div className="text-center p-2 text-sm text-gray-800">15</div>
                  <div className="text-center p-2 text-sm text-gray-800">16</div>
                  <div className="text-center p-2 text-sm text-gray-800">17</div>
                  <div className="text-center p-2 text-sm text-gray-800">18</div>
                  
                  {/* Week 4 */}
                  <div className="text-center p-2 text-sm text-gray-800">19</div>
                  <div className="text-center p-2 text-sm text-gray-800">20</div>
                  <div className="text-center p-2 text-sm text-gray-800">21</div>
                  <div className="text-center p-2 text-sm text-gray-800">22</div>
                  <div className="text-center p-2 text-sm text-gray-800">23</div>
                  <div className="text-center p-2 text-sm text-gray-800">24</div>
                  <div className="text-center p-2 text-sm text-gray-800">25</div>
                  
                  {/* Week 5 */}
                  <div className="text-center p-2 text-sm text-gray-800">26</div>
                  <div className="text-center p-2 text-sm text-gray-800">27</div>
                  <div className="text-center p-2 text-sm text-gray-800">28</div>
                  <div className="text-center p-2 text-sm text-gray-800">29</div>
                  <div className="text-center p-2 text-sm text-gray-800">30</div>
                  <div className="text-center p-2 text-sm text-gray-800">31</div>
                </div>
              </div>
            </div>
          )}

          {/* Important Notes Section */}
          <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
            <h3 className="font-runtime text-2xl font-bold text-black mb-6 flex items-center">
              <Info className="h-6 w-6 text-dpn-pink mr-3" />
              Important Notes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-dpn-pink rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="font-lucy-rose text-gray-700">
                    <strong>Mahalaya</strong> is observed a week before the main Puja begins—it marks the invocation of Goddess Durga and the spiritual commencement of the period.
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-dpn-light-green rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="font-lucy-rose text-gray-700">
                    The five central days—<strong>Shashthi to Vijayadashami</strong>—represent the core of Durga Puja celebrations, involving rituals like Sandhi Puja, Kumari Puja, and Sindoor Khela.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-dpn-raspberry rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="font-lucy-rose text-gray-700">
                    <strong>Durga Puja in 2025</strong> officially runs from 28 September to 2 October, though earlier rituals starting from 21 September set the tone.
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-dpn-olive rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="font-lucy-rose text-gray-700">
                    <strong>Vijaya Dashami</strong> on 2 October marks the end of the festival, culminating in idol immersion and farewell ceremonies.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-dpn-pink to-dpn-raspberry rounded-2xl p-8 text-white">
              <h3 className="font-runtime text-2xl font-bold mb-4">Join the Global Celebration</h3>
              <p className="font-lucy-rose text-lg opacity-95 mb-6 max-w-2xl mx-auto">
                Connect with Durga Puja celebrations worldwide and be part of this magnificent cultural heritage.
              </p>
              <button 
                onClick={goToRegistration}
                className="bg-white text-dpn-raspberry hover:bg-gray-100 font-lucy-rose font-semibold px-8 py-3 rounded-full transition-colors duration-200"
              >
                Join The Network
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Calendar;